import React, { useCallback, useContext, useState } from 'react';
import { noop } from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { DefaultButton, IconButton, Label, MessageBar, Panel, PanelType, PrimaryButton, TextField } from '@fluentui/react';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { helpbuttonStyle, convertToLocalStyle } from '@bingads-webui-campaign-react/common-styles';
import { createBrandFromProp, GENERIC_ERROR_MESSAGE, getOdataResponseErrors } from '@bingads-webui-campaign-react/brand-lists-data';
import { useBrandsData } from '../brands-picker/brands-data';

const getStyles = (_, { palette = {} }) => ({
  RequestBrandPanel: {
    '& .brand-description': {
      '& .ms-MessageBar': {
        background: '#EFF6FC',
        marginBottom: 16,
      },
    },
    '& .brand-name-length': {
      display: 'flex',
      justifyContent: 'flex-end',
      margin: '2px 0',
    },
    '& .ms-Panel-header': {
      padding: 0,
    },
    '& .error-message': {
      color: palette.redDark,
      marginBottom: 16,
    },
    ...convertToLocalStyle(helpbuttonStyle({ palette })),
  },
  BackButton: {
    paddingLeft: 32,
    paddingRight: 18,
  },
  Form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    margin: '16px 0',
  },
  FormItem: {
  },
  SaveButton: {
    marginRight: 8,
  },
});

const BRAND_NAME_MAX_LENGTH = 128;

export const RequestBrandPanel = ({
  isOpen, onDismiss, cid, aid,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  const {
    selectedOptions,
    setSelectedOptions,
    searchFilter,
  } = useBrandsData();

  const [brandName, setBrandName] = useState(searchFilter || '');
  const [brandLink, setBrandLink] = useState('');
  const [brandReason, setBrandReason] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);

  const onRenderHeader = useCallback((props, defaultRender) => (
    <>
      <IconButton
        className={classNames(classes.BackButton, 'back-button')}
        iconProps={{ iconName: 'Back' }}
        ariaLabel={i18n.getString(_TL_('Close'))}
        onClick={onDismiss}
      />
      {defaultRender(props)}
    </>
  ), [classes.BackButton, i18n, onDismiss]);

  const onSaveBrand = useCallback((result) => {
    if (selectedOptions.some(option => option.key === result.key)) {
      return;
    }
    setSelectedOptions([...selectedOptions, result]);
  }, [selectedOptions, setSelectedOptions]);

  const onSave = useCallback(async () => {
    try {
      const saveCallPromise = createBrandFromProp({ cid, aid });
      const result = await saveCallPromise({ brandName, brandLink, brandReason });
      if (result && result.value && result.value.length > 0) {
        const newBrand = result.value[0];
        onSaveBrand({
          key: newBrand.Id,
          name: newBrand.Name,
          link: newBrand.Uri,
          isUnderReview: true,
        });
        onDismiss();
      } else {
        setErrorMsg(i18n.getString(GENERIC_ERROR_MESSAGE));
      }
    } catch (error) {
      const serverErrors = getOdataResponseErrors(i18n, error);
      setErrorMsg(serverErrors[0].message);
    }
  }, [cid, aid, brandName, brandLink, brandReason, onSaveBrand, onDismiss, i18n]);

  const onRenderFooterContent = useCallback(() => (
    <div>
      <PrimaryButton className={classNames(classes.SaveButton, 'save-button')} disabled={!brandName || !brandLink} onClick={onSave}>
        {i18n.getString(_TL_('Submit'))}
      </PrimaryButton>
      <DefaultButton className={classNames(classes.CancelButton, 'cancel-button')} onClick={onDismiss}>
        {i18n.getString(_TL_('Cancel'))}
      </DefaultButton>
    </div>
  ), [brandLink, brandName, classes.CancelButton, classes.SaveButton, i18n, onDismiss, onSave]);

  return (
    <Panel
      className={classNames(classes.RequestBrandPanel, 'request-brand-panel')}
      closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
      type={PanelType.medium}
      isOpen={isOpen}
      onDismiss={onDismiss}
      headerText={i18n.getString(_TL_('Request a brand'))}
      onRenderHeader={onRenderHeader}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom
      // this is to work around a bug in Fluent UI where the panel will close when closing the dialog, https://github.com/microsoft/fluentui/issues/6476
      onOuterClick={noop}
    >
      <div className={classNames(classes.Form, 'brand-content')}>
        <div className={classNames(classes.FormItem, 'brand-description')}>
          <MessageBar className="info-msg-bar">
            {i18n.getString(_TL_('It takes up to a week to review a brand request. If approved, it’ll be added to your shared library. Please note that you can only apply brand protection or brand exclusion to a brand once it’s approved.'))}
          </MessageBar>
        </div>
        <div className={classNames(classes.FormItem, 'brand-name')}>
          <Label htmlFor="brand_name">
            {i18n.getString(_TL_('Brand name'))}
            <HelpPopup topic="pop_BA_BrandLists_BrandRequest" aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: i18n.getString(_TL_('Brand name')) })} />
          </Label>
          <TextField
            id="brand_name"
            className="brand-name-input"
            placeholder={i18n.getString(_TL_('Enter brand name'))}
            value={brandName}
            onChange={(e, value) => {
              setBrandName(value && value.length > BRAND_NAME_MAX_LENGTH ? value.slice(0, BRAND_NAME_MAX_LENGTH) : value);
            }}
          />
          <p className="brand-name-length">
            {i18n.getString(_TL_('{{count}} characters remaining'), {
              count: BRAND_NAME_MAX_LENGTH - brandName.length,
            })}
          </p>
        </div>
        <div className={classNames(classes.FormItem, 'brand-url')}>
          <Label htmlFor="brand_url">
            {i18n.getString(_TL_('Brand URL'))}
            <HelpPopup topic="pop_BA_BrandLists_URL" aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: i18n.getString(_TL_('Brand URL')) })} />
          </Label>
          <TextField
            id="brand_url"
            className="brand-url-input"
            placeholder={i18n.getString(_TL_('Enter brand URL starting with http:// or https://'))}
            value={brandLink}
            onChange={(e, value) => setBrandLink(value)}
          />
        </div>
        <div className={classNames(classes.FormItem, 'brand-reason')}>
          <Label htmlFor="brand_reason">
            {i18n.getString(_TL_('Reasons to request this brand'))}
          </Label>
          <TextField
            id="brand_reason"
            className="brand-reason-input"
            placeholder={i18n.getString(_TL_('To help with the review process, provide detailed information about why you’re requesting this brand.'))}
            value={brandReason}
            onChange={(e, value) => setBrandReason(value)}
            multiline
            rows={6}
            resizable={false}
          />
        </div>
        {errorMsg && <div className="error-message">{errorMsg}</div>}
      </div>
    </Panel>
  );
};

RequestBrandPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
};
